import { ComponentType, lazy } from 'react'
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom'
import { AdaptiveLayout, AuthLayout, MainFullLayout, MainLayout } from './components/layouts'
import AnnouncementPage from './components/pages/AnnouncementPage'
import AuthSuccess from './components/pages/auth-success/AuthSuccess'
import IndexPage from './components/pages/IndexPage'
import Login from './components/pages/login/Login'
import ForgotPassword from './components/pages/login/ForgotPassword'
import ResetPassword from './components/pages/login/ResetPassword'
import NotFoundPage from './components/pages/NotFoundPage'
import NotificationsPage from './components/pages/NotificationsPage'
import RegistrationPage from './components/pages/RegistrationPage'
import WishlistPage from './components/pages/WishlistPage'
import { CreateRequestWithoutAuth } from './components/pages/login/CreateRequestWithoutAuth'
import { AuthGuard } from './components/layouts/auth/AuthGuard'
import CompleteRegistration from './components/pages/completeRegistration/completeRegistration'
import SupportRedirect from './components/pages/support/SupportRedirect'
import PaymentResult from './components/pages/payment-result/PaymentResult'
import PrivacyPolicy from './components/pages/PrivacyPolicy/PrivacyPolicy'
import RouterErrorBoundary from './components/elements/error-boundary/RouterErrorBoundary'

// A dynamic-import failure right after a redeploy means the open tab holds an
// old index.html whose hashed chunk is gone from the server. One transparent
// reload picks up the fresh build; the sessionStorage flag prevents a reload
// loop when the network is genuinely down.
const lazyWithReload = <T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) =>
  lazy(() =>
    factory()
      .then((module) => {
        sessionStorage.removeItem('chunk-reload')
        return module
      })
      .catch((error) => {
        if (!sessionStorage.getItem('chunk-reload')) {
          sessionStorage.setItem('chunk-reload', '1')
          window.location.reload()
          return new Promise<never>(() => {})
        }
        throw error
      }),
  )

// Heavy routes are code-split: every layout already wraps its Outlet in
// <Suspense>, so the lazy chunks stream in behind the shared Loader. The
// agreement generator (1700+ lines with the rental form), the map stack, the
// wizard, the profile and the messenger dominate the main bundle otherwise.
const MapPage = lazyWithReload(() => import('./components/pages/MapPage'))
const CreateAdPage = lazyWithReload(() => import('./components/pages/CreateAdPage'))
const ProfilePage = lazyWithReload(() => import('./components/pages/ProfilePage'))
const Messenger = lazyWithReload(() => import('./components/pages/messenger/Messenger'))
const ChatWindow = lazyWithReload(() =>
  import('./components/pages/messenger/components').then((m) => ({ default: m.ChatWindow })),
)
const GenerateAgreementPage = lazyWithReload(
  () => import('./components/pages/agreement/GenerateAgreementPage'),
)

// errorElement attached at the root only. createBrowserRouter walks up the
// route tree on uncaught throws, so a root-level boundary covers every
// descendant. Per-route boundaries would be more granular but every page
// currently wants the same fallback ("reload page" + "home"), so a single
// shared element is enough and keeps the route tree readable.
export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RouterErrorBoundary />}>
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route element={<AuthGuard />}>
        <Route element={<AuthLayout />}>
          <Route path="/registration" element={<RegistrationPage />} />
          <Route path="/complete-registration" element={<CompleteRegistration />} />
          <Route path="/login" element={<Login />}>
            <Route path="create-request-without-auth" element={<CreateRequestWithoutAuth />} />
          </Route>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth-success" element={<AuthSuccess />} />
        </Route>
        <Route element={<MainLayout />}>
          {/* The former home page: personal blocks (requests/offers/listings)
              live in the cabinet now that the map is the front page (38.3) */}
          <Route path="/cabinet" element={<IndexPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/create-ad" element={<CreateAdPage />} />
          {/* Full-page listing edit: the same wizard prefilled (roadmap 40) */}
          <Route path="/edit-ad/:id" element={<CreateAdPage />} />
          <Route path="/agreement" element={<GenerateAgreementPage />} />
          <Route path="/support" element={<SupportRedirect />} />
          {/* Monobank sends the buyer back here after checkout */}
          <Route path="/payment/result" element={<PaymentResult />} />
        </Route>
        {/* Guest-browsable routes: authenticated users get the full shell,
            guests get the public chrome (see AdaptiveLayout). The map IS the
            front page for everyone; /map stays as an alias for old links. The
            listing detail page stays shareable/indexable for guests but keeps
            the app menu for logged-in users (owner feedback). */}
        <Route element={<AdaptiveLayout />}>
          <Route path="/" element={<MapPage />} />
          <Route path="/map" element={<MapPage />} />
          <Route path="/announcement/:id" element={<AnnouncementPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route element={<MainFullLayout />}>
          <Route path="/messenger" element={<Messenger />}>
            <Route path=":id" element={<ChatWindow />} />
          </Route>
        </Route>
      </Route>
    </Route>,
  ),
)
